'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function AltıNoktaLiftingNedirYuzGenclestirmedeYeniNesilTeknikPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/6-nokta-lifting-nedir-yuz-genclestirmede-yeni-nesil-teknik';
  const pageTitle = '6 Nokta Lifting Nedir? Yüz Gençleştirmede Yeni Nesil Teknik';
  const pageDescription = '6 Nokta Lifting, yüzün stratejik bölgelerine uygulanan özel dolgu tekniğiyle cerrahi olmayan lifting etkisi sağlar. Nasıl yapılır, kimlere uygundur, kalıcılığı ne kadardır? Tüm detaylar burada.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: '6 Nokta Lifting Nedir?', href: '/tr/blog/6-nokta-lifting-nedir-yuz-genclestirmede-yeni-nesil-teknik' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="6 Nokta Lifting işlemi ve yüz gençleştirme değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                6 Nokta Lifting
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              6 Nokta Lifting Nedir? Yüz Gençleştirmede Yeni Nesil Teknik
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>27 Ocak 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>9 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Yüz yaşlanması, ciltteki elastikiyet kaybı, hacim azalması ve sarkma gibi faktörlerle oluşur. 6 Nokta Lifting (ya da "6 Point Lift") bu sorunlara çözüm olarak geliştirilen cerrahi olmayan yüz gençleştirme yöntemidir. Yüzün belirli altı stratejik noktasına yapılan özel dolgu enjeksiyonları sayesinde, doğal bir lifting (yukarı çekme) etkisi elde edilir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed font-medium">
                Bu teknik, klasik dolgudan farklı olarak yalnızca kırışıklıkları doldurmakla kalmaz; aynı zamanda yüzün genel mimarisini yeniden şekillendirir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. 6 Nokta Lifting Nasıl Yapılır?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Uygulama, yüzün yaşlanmadan en fazla etkilenen 6 destek noktasına özel bir dolgu formülüyle yapılır. Bu noktalar anatomik olarak yüzün yukarı yönlü taşıyıcı bölgeleridir.
              </p>

              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-purple-900 mb-3">Uygulama noktaları:</h3>
                <ul className="list-disc list-inside text-purple-800 space-y-2">
                  <li>Elmacık kemiği (malar bölge)</li>
                  <li>Nazolabial (burun-dudak arası) destek noktası</li>
                  <li>Ağız köşesi hattı</li>
                  <li>Çene hattı başlangıcı</li>
                  <li>Mandibular açı (çene kemiği)</li>
                  <li>Şakak veya yan yüz desteği</li>
                </ul>
                <p className="text-purple-800 mt-3">
                  Bu noktalara yapılan enjeksiyon, yüzün yukarı doğru gerilmesini ve daha gergin bir görünüm oluşmasını sağlar.
                </p>
              </div>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <div className="flex items-center space-x-4">
                  <span className="text-2xl">🕒</span>
                  <span className="text-blue-800 font-medium">İşlem süresi ortalama 20–30 dakika</span>
                </div>
                <div className="flex items-center space-x-4 mt-2">
                  <span className="text-2xl">💤</span>
                  <span className="text-blue-800 font-medium">Lokal anestezik kremle ağrısız bir uygulamadır</span>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. 6 Nokta Lifting ile Klasik Dolgu Arasındaki Fark
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Özellik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        6 Nokta Lifting
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Klasik Dolgu
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Amaç</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Yüzü yukarı taşımak ve form kazandırmak</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hacim eklemek, kırışıklık doldurmak</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Uygulama Noktası</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Yüzün taşıyıcı 6 stratejik bölgesi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Sorunlu kırışıklık bölgeleri</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Sonuç</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Doğal lifting, sıkı görünüm</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Lokal dolgunluk</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Etkisi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">12–18 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">6–12 ay</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">İyileşme Süresi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hemen sosyal yaşama dönüş</td>
                      <td className="px-6 py-4 text-sm text-gray-900">1–2 gün hafif ödem</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Etki Süresi ve Kalıcılık
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                6 Nokta Lifting uygulamasında kullanılan dolgu genellikle hyaluronik asit bazlıdır ve etkisi 12–18 ay sürer. Ancak etkisini uzatan faktörler:
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-green-800 space-y-2">
                  <li>Uygulama sonrası cilt bakımı</li>
                  <li>Güneşten korunma</li>
                  <li>Sigara ve alkol tüketiminden kaçınma</li>
                  <li>Düzenli nemlendirme ve su tüketimi</li>
                </ul>
                <p className="text-green-800 mt-3 font-medium">
                  İlk seansın ardından 12–15 ay sonra idame seansı önerilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Kimler İçin Uygundur?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Bu teknik özellikle:
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-blue-900 mb-3">Uygun adaylar:</h3>
                  <ul className="list-disc list-inside text-blue-800 space-y-2">
                    <li>30–60 yaş arası</li>
                    <li>Cildinde hafif sarkma ve hacim kaybı olan</li>
                    <li>Cerrahi müdahaleden kaçınan kişiler</li>
                    <li>Yüz simetrisini koruyarak doğal bir gençleşme isteyen hastalar</li>
                  </ul>
                </div>

                <div className="bg-red-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-red-900 mb-3">Uygulanmaz:</h3>
                  <ul className="list-disc list-inside text-red-800 space-y-2">
                    <li>Hamilelik, emzirme</li>
                    <li>Aktif enfeksiyon</li>
                    <li>Kan sulandırıcı ilaç kullanımı</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. İşlem Sonrası Dikkat Edilmesi Gerekenler
              </h2>
              <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-yellow-800 space-y-2">
                  <li>İlk 24 saat yüzünüze masaj veya baskı uygulamayın</li>
                  <li>İlk 48 saat sauna, hamam, ağır egzersizden kaçının</li>
                  <li>Yeterli su tüketimi ve düzenli uyku cilt yenilenmesini destekler</li>
                </ul>
                <p className="text-yellow-800 mt-3 font-medium">
                  Bu öneriler, dolgunun daha uzun süre kalıcı olmasını sağlar.
                </p>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="6 Nokta Lifting işlemi ve yüz gençleştirme"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. 6 Nokta Lifting yüzü şişirir mi?</h3>
                  <p className="text-gray-700">Hayır, amaç yüzü hacimlendirmek değil, destek noktalarından yukarı taşımaktır. Sonuç doğaldır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Etkisi ne kadar sürer?</h3>
                  <p className="text-gray-700">Kişiye göre değişmekle birlikte ortalama 12–18 ay kalıcılıktadır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Ne zaman sonuç görülür?</h3>
                  <p className="text-gray-700">Etkisi hemen fark edilir, ödem azaldıkça nihai görünüm 3–5 gün içinde oturur.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. 6 Nokta Lifting acılı mı?</h3>
                  <p className="text-gray-700">Hayır. Uygulama öncesi lokal anestezik krem kullanılır, ağrı minimaldir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Diğer işlemlerle kombine edilebilir mi?</h3>
                  <p className="text-gray-700">Evet, botoks, PRP veya mezoterapiyle birlikte uygulandığında daha uzun süreli sonuç alınır.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>European Academy of Aesthetic Medicine (EAAM) – "6 Point Lift Injection Techniques"</li>
                <li>Türk Estetik Tıp Derneği (TETD) – Dolgu ve Lifting Protokolleri</li>
                <li>Mayo Clinic – "Hyaluronic Acid Fillers and Structural Lifting"</li>
                <li>Harvard Health Publishing – "Modern Approaches to Non-Surgical Facelifting"</li>
                <li>ISAPS Global Survey 2024 – "Injectable Lifting Trends"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#6noktalifting</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#yüzgençleştirme</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#dolgu</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#ameliyatsızestetik</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#hyaluronikasit</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#lifting</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">6 Nokta Lifting Uygulaması İçin Randevu Alın</h3>
              <p className="mb-6">Cerrahi olmayan yüz gençleştirme ve lifting etkisi için uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('6 Nokta Lifting uygulaması hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-purple-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/sivi-yuz-germe-nedir-hangi-icerikli-urunler-kullanilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Sıvı Yüz Germe Nedir?</h4>
              <p className="text-gray-600 text-sm">Sıvı yüz germe işlemi ve kullanılan içerikler hakkında bilgiler.</p>
            </Link>
            <Link
              href="/tr/blog/dolgu-islemleri-yuz-liftingi-saglar-mi"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dolgu İşlemleri Yüz Liftingi Sağlar mı?</h4>
              <p className="text-gray-600 text-sm">Dolgu uygulamalarının yüz liftingi etkisi hakkında bilgiler.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

